import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetCompanyById } from "@/services/Company";
import { AdminCompanyListItem, Company } from "@/types";
import { CompanyDecisionKind, getDecisionKind } from "@/components/pages/CompanyAdmin/pendingActions";

import Drawer, { DrawerHeader } from "@/components/molecules/Drawer";
import DrawerTabs, { DrawerTab } from "@/components/atoms/DrawerTabs";
import CreditTab from "./Tabs/Credit";
import Loader from "@/components/atoms/Loader";
import SharedCompanyInfoTab from "../SharedTabs/SharedCompanyInfo";
import CompanyPendingActionTypeEnum from "@/constants/enums/CompanyPendingActionTypeEnum";
import Group from "@/components/atoms/Group";
import CTA from "@/components/atoms/CTA";
import Tooltip from "@/components/atoms/Tooltip";
import EconomyTab from "./Economy";
import SettingsTab from "./Tabs/Settings";
import AMLandKYCTab from "../SharedTabs/AMLandKYC";
import InvitedCompanyActions from "./actions/InvitedCompanyActions";
import RegisteredCompanyActions from "./actions/RegisteredCompanyActions";

interface CompanyDrawerProps {
  adminCompanyListItem: AdminCompanyListItem | null;
  companyId: number;
  onClose: () => void;
  isOpen: boolean;
}

export default function CompanyDrawer({ adminCompanyListItem, onClose, isOpen, companyId }: CompanyDrawerProps) {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState<string | null>("companyInfo");

  const { data: companyData, isFetching: isFetchingCompany } = useGetCompanyById(companyId, {
    enabled: companyId > 0 && isOpen,
    refetchOnWindowFocus: false,
  });

  const hasPendingCreditAction =
    adminCompanyListItem?.pendingActions &&
    adminCompanyListItem.pendingActions.find(
      (action) => action.type === CompanyPendingActionTypeEnum.CREDIT_REQUEST && action.status === "OPEN"
    );
  const decisionKind = getDecisionKind(adminCompanyListItem);

  const isKycScreeningEnabled = !!companyData?.kycScreeningEnabled;
  const visibleTab = activeTab === "AML" && !isKycScreeningEnabled ? "companyInfo" : activeTab;

  return (
    <Drawer
      onClose={onClose}
      isOpen={isOpen}
      title={<DrawerHeader title={t("common:company")} svgName="companyXl" />}
      ctaRow={<DecisionActions kind={decisionKind} company={companyData} onClose={onClose} />}
    >
      <>
        <DrawerTabs value={visibleTab} onChange={setActiveTab}>
          <DrawerTabs.List style={{ position: "sticky" }}>
            <DrawerTabs.Tab value="companyInfo">
              <DrawerTab label={t("company:companyInformation")} />
            </DrawerTabs.Tab>

            <DrawerTabs.Tab
              value="credit"
              style={{
                backgroundColor: hasPendingCreditAction ? "#fffbe6" : undefined,
                borderTop: hasPendingCreditAction ? "1px solid #ffe58f" : undefined,
              }}
            >
              <DrawerTab svgName="economy" label={t("common:credit")} />
            </DrawerTabs.Tab>

            <DrawerTabs.Tab value="settings">
              <DrawerTab svgName="settings" label={t("common:settings")} />
            </DrawerTabs.Tab>

            <DrawerTabs.Tab value="economy">
              <DrawerTab svgName="economy" label={t("common:economy")} />
            </DrawerTabs.Tab>

            {isKycScreeningEnabled ? (
              <DrawerTabs.Tab value="AML">
                <DrawerTab label="AML/KYC" />
              </DrawerTabs.Tab>
            ) : (
              // Disabled buttons swallow pointer events, so the tooltip is anchored on a wrapper.
              <Tooltip label={t("commonAdmin:kycScreening.notEnabled")}>
                <span style={{ display: "inline-flex" }} data-testid="aml-tab-disabled">
                  <DrawerTabs.Tab value="AML" disabled>
                    <DrawerTab label="AML/KYC" />
                  </DrawerTabs.Tab>
                </span>
              </Tooltip>
            )}
          </DrawerTabs.List>

          {!isFetchingCompany && companyData ? (
            <>
              <DrawerTabs.Panel value="companyInfo">
                <SharedCompanyInfoTab company={companyData} />
              </DrawerTabs.Panel>

              <DrawerTabs.Panel value="credit">
                <CreditTab
                  company={companyData}
                  onCloseDrawer={onClose}
                  hasPendingCreditAction={hasPendingCreditAction}
                />
              </DrawerTabs.Panel>

              <DrawerTabs.Panel value="settings">
                <SettingsTab company={companyData} />
              </DrawerTabs.Panel>

              <DrawerTabs.Panel value="economy">
                <EconomyTab company={companyData} isActive={activeTab === "economy"} />
              </DrawerTabs.Panel>

              {isKycScreeningEnabled && (
                <DrawerTabs.Panel value="AML">
                  <AMLandKYCTab company={companyData} />
                </DrawerTabs.Panel>
              )}
            </>
          ) : (
            <Loader />
          )}
        </DrawerTabs>
      </>
    </Drawer>
  );
}

const DecisionActions = ({
  kind,
  company,
  onClose,
}: {
  kind: CompanyDecisionKind | null;
  company?: Company;
  onClose: () => void;
}) => {
  const { t } = useTranslation();

  if (kind === "registered") return <RegisteredCompanyActions company={company} onClose={onClose} />;
  if (kind === "invited") return <InvitedCompanyActions company={company} onClose={onClose} />;
  return (
    <Group align={"flex-start"} justify={"flex-start"} w={"100%"}>
      <CTA intent="secondary" onClick={onClose}>
        {t("actions:close")}
      </CTA>
    </Group>
  );
};
